import React, { useCallback, useMemo, useState } from "react"
import PropTypes from "prop-types"
import dayjs from "dayjs"
import isBetween from "dayjs/plugin/isBetween"
import { useTranslation } from "react-i18next"
import { DayPicker } from "react-day-picker"
import { Typography, Tooltip, Flex } from "antd"
import "react-day-picker/style.css"
import { countDays } from "pagesV2/pro/Bookings/helpers/multiDayPricing"
import { useDailyDiscountCalendar } from "pagesV2/pro/Bookings/helpers/useDailyDiscountCalendar"
import { numberToCurrency } from "helpers/string"
import styles from "./BookingCalendar.module.css"

dayjs.extend(isBetween)

const { Text } = Typography

const ISO = "YYYY-MM-DD"

/**
 * Inline booking calendar, shared by the guest/embedded flow and the guide's manual booking form.
 *
 * Three shapes, driven by the experience:
 *  - single day        → one date ("Select Start Date")
 *  - fixed multi-day   → one date, but the chosen duration paints the whole block it covers
 *                        (`spanDays`); the picker chooses a start, not an end
 *  - dynamic multi-day → a start/end range ("Select Start and End Date"), constrained
 *                        by the experience's min/max duration
 */
const DateSelector = ({
  value,
  onChange,
  collapsed = false,
  error,
  errorMessage,
  trip,
  isRange,
  minDays,
  maxDays,
  maxSpanByDate,
  availableDates,
  bookedDates,
  rangeValue,
  label,
  spanDays = 0
}) => {
  const [t] = useTranslation()
  const [blockedHint, setBlockedHint] = useState(null)
  // rangeStart/rangeEnd wire the picked stay into the discount calculation so progressive
  // duration pricing (a day-of-stay ramp, not a calendar-date property) can be dynamically
  // evaluated once the range is COMPLETE — day/seasonal keep evaluating on each date's own
  // calendar day regardless. Only passed once both ends are picked (rangeEnd undefined until
  // then), so a lone start date doesn't show progressive-adjusted prices with nothing to
  // index them against yet. Deliberately not passed by the experience-preview calendar, which
  // has no picked stay to index from. See useDailyDiscountCalendar's options doc.
  const { hasPricing, priceInfoForDate, isFlatRate } = useDailyDiscountCalendar(trip, undefined, {
    rangeStart: isRange ? rangeValue?.from : undefined,
    rangeEnd: isRange ? rangeValue?.to : undefined
  })

  /**
   * How long a stay may actually run from the chosen start. The backend reports the real figure per
   * start date (consecutive free days, capped at the trip's maximum); without it a guest can pick a
   * span the validator then rejects on a date the calendar showed as available.
   */
  const effectiveMaxDays = useMemo(() => {
    const from = rangeValue?.from
    if (!from || !maxSpanByDate) return maxDays
    const allowed = Number(maxSpanByDate[dayjs(from).format(ISO)])
    return allowed > 0 ? Math.min(allowed, maxDays || allowed) : maxDays
  }, [maxSpanByDate, maxDays, rangeValue?.from])

  /**
   * Start dates the backend says are actually bookable, and dates already taken. When the server
   * supplies these they win: it accounts for whether a whole stay fits from a given day, which the
   * trip-level weekday/season fields below cannot express. An empty or missing list falls back to
   * the local rules rather than disabling the whole calendar.
   */
  const allowedStarts = useMemo(() => (availableDates?.length ? new Set(availableDates) : null), [availableDates])
  const takenDates = useMemo(() => (bookedDates?.length ? new Set(bookedDates) : null), [bookedDates])

  const isDayUnavailable = useCallback(
    (current) => {
      const today = dayjs().startOf("day")
      const date = dayjs(current)
      const scheduledDeletionDate = trip?.guide?.scheduled_deletion_date

      if (!current || !trip) return true

      const iso = date.format(ISO)
      if (takenDates?.has(iso)) return true

      // Once a range has a start — whether or not an end is picked yet — every day on or after
      // it is a candidate END of that stay, not a fresh START, INCLUDING re-picking/extending an
      // already-complete range (DayPicker's addToRange treats a later click as moving `to`, not
      // starting over). The backend's start-day list (and the local weekday/season fallback
      // below) only say where a NEW stay may legally begin, and the stay runs continuously from
      // the chosen start regardless. Only real unavailability (already booked, checked above)
      // should stop it; DayPicker's own min/max (wired from minDays/effectiveMaxDays) already
      // enforces the duration range itself. A day BEFORE the current start still goes through
      // the normal check below, since clicking one moves the start itself, which does need to be
      // a legal start day.
      if (isRange && rangeValue?.from && !date.isBefore(dayjs(rangeValue.from), "day")) return false

      if (allowedStarts) return date.isBefore(today) || !allowedStarts.has(iso)

      const weekdayMap = {
        sunday: 0,
        monday: 1,
        tuesday: 2,
        wednesday: 3,
        thursday: 4,
        friday: 5,
        saturday: 6
      }

      const blockedWeekdays = trip?.unavailable_weekdays?.map((day) => weekdayMap[day.toLowerCase()]) || []

      const isSoldOut = trip?.sold_out_dates?.some((dateStr) => date.isSame(dayjs(dateStr), "day"))
      const isUnavailable = trip?.unavailable_dates?.some((dateStr) => date.isSame(dayjs(dateStr), "day"))

      const hasDateRanges = Array.isArray(trip?.available_date_ranges) && trip.available_date_ranges.length > 0

      let isInAvailableRange = true
      if (hasDateRanges) {
        isInAvailableRange = trip.available_date_ranges.some((range) => {
          const start = dayjs(range.start).startOf("day")
          const end = dayjs(range.end).endOf("day")
          return date.isBetween(start, end, null, "[]")
        })
      }

      return (
        date.isBefore(today) ||
        date.isAfter(scheduledDeletionDate) ||
        blockedWeekdays.includes(date.day()) ||
        isUnavailable ||
        isSoldOut ||
        (hasDateRanges && !isInAvailableRange)
      )
    },
    [trip, allowedStarts, takenDates, isRange, rangeValue?.from]
  )

  /**
   * The block a fixed duration covers, painted as a range even though only one day is clicked:
   * first and last day filled, the days between banded.
   */
  const startIso = value ? dayjs(value).format(ISO) : ""
  const spanModifiers = useMemo(() => {
    if (spanDays < 2 || !startIso) return undefined
    const start = dayjs(startIso).startOf("day")
    const end = start.add(spanDays - 1, "day")
    return {
      spanStart: start.toDate(),
      spanEnd: end.toDate(),
      spanMiddle: { after: start.toDate(), before: end.toDate() }
    }
  }, [spanDays, startIso])

  /**
   * While a range is half-selected, hovering a day that would make the range too short or too long
   * explains why it will not take, instead of the click silently doing nothing.
   */
  const handleDayMouseEnter = useCallback(
    (date) => {
      if (!isRange) return
      const start = rangeValue?.from
      if (!start || rangeValue?.to) {
        setBlockedHint(null)
        return
      }
      if (dayjs(date).isBefore(dayjs(start), "day")) {
        setBlockedHint(null)
        return
      }
      const days = countDays(start, date)
      if (minDays && days < minDays) setBlockedHint({ iso: dayjs(date).format(ISO), text: t("booking.multi_day.min_days", { count: minDays }) })
      else if (effectiveMaxDays && days > effectiveMaxDays)
        setBlockedHint({ iso: dayjs(date).format(ISO), text: t("booking.multi_day.max_days", { count: effectiveMaxDays }) })
      else setBlockedHint(null)
    },
    [isRange, rangeValue?.from, rangeValue?.to, minDays, effectiveMaxDays, t]
  )

  const DayButton = useCallback(
    ({ day, modifiers, children, ...buttonProps }) => {
      const isBlocked = blockedHint?.iso === dayjs(day.date).format(ISO)
      const info = hasPricing ? priceInfoForDate(dayjs(day.date)) : null
      // The literal day(s) the guest clicked — a single date, or a range's two endpoints, or
      // (fixed-duration single-click block) the span's first/last day. react-day-picker paints
      // its own selected/range-endpoint fill on the whole button by default, which is why a
      // second (price) line used to clip against the circle's curved edge — that fill is
      // neutralized in CSS, and painted here instead on just the day-number span, so the price
      // pill below it has its own room.
      const isSelected = Boolean(
        modifiers?.selected || modifiers?.range_start || modifiers?.range_end || modifiers?.spanStart || modifiers?.spanEnd
      )

      const button = (
        <button {...buttonProps} className={`${buttonProps.className || ""} ${styles.calendarDayButton}`}>
          <span className={isSelected ? styles.calendarDayNumberSelected : styles.calendarDayNumber}>{children}</span>
          {info?.hasDiscount && (
            <span className={isSelected ? styles.calendarDayPriceSelected : styles.calendarDayPrice}>
              {numberToCurrency(info.headline)}
            </span>
          )}
          {info?.hasDiscount && <span className={styles.calendarDayDot} />}
        </button>
      )

      // The "range too short/long" hint (while actively building a range) takes priority
      // over the discount breakdown — it's live feedback about the click that just happened.
      if (isBlocked) {
        return (
          <Tooltip open title={blockedHint.text} placement="top">
            {button}
          </Tooltip>
        )
      }

      if (!info?.hasDiscount) return button

      return (
        <Tooltip
          placement="top"
          title={
            <Flex vertical gap={4} className={styles.discountTooltipContent}>
              <Text strong className={styles.discountTooltipHeading}>
                {isFlatRate
                  ? t("listings.tabs.preview.pricing_preview.min_price", { defaultValue: "Min. price:" })
                  : t("listings.tabs.preview.pricing_preview.min_price_per_person", {
                      defaultValue: "Min. price per person:"
                    })}
              </Text>
              {info.rows.map((row) => (
                <Flex key={row.key} justify="space-between" gap={12}>
                  <span className={styles.discountTooltipLabel}>{row.label}</span>
                  <span>
                    <Text delete className={styles.discountTooltipOriginal}>
                      {numberToCurrency(row.original)}
                    </Text>
                    <Text strong className={styles.discountTooltipAdjusted}>
                      {numberToCurrency(row.adjusted)}
                    </Text>
                  </span>
                </Flex>
              ))}
            </Flex>
          }
        >
          {button}
        </Tooltip>
      )
    },
    [blockedHint, hasPricing, priceInfoForDate, isFlatRate, t]
  )

  if (collapsed) {
    let summary = value && dayjs(value).format("MM/DD/YY")
    if (isRange) {
      summary = [rangeValue?.from, rangeValue?.to].filter(Boolean).map((date) => dayjs(date).format("MM/DD/YY")).join(" – ")
    } else if (value && spanDays >= 2) {
      summary = `${summary} – ${dayjs(value).add(spanDays - 1, "day").format("MM/DD/YY")}`
    }
    return (
      <div className={styles.inputContainer}>
        <span className={styles.inputLabel}>{t("booking.multi_day.date_label")}</span>
        <span className={styles.summaryValue}>{summary || "—"}</span>
      </div>
    )
  }

  const commonProps = {
    captionLayout: "label",
    // Availability gates the day that is picked. A multi-day block runs continuously from there,
    // so the days it covers are not re-checked — an experience that only runs Wed–Fri would
    // otherwise have no legal start at all for anything longer than three days.
    disabled: isDayUnavailable,
    startMonth: dayjs().startOf("month").toDate(),
    showOutsideDays: true,
    onDayMouseEnter: handleDayMouseEnter,
    onDayMouseLeave: () => setBlockedHint(null),
    components: { DayButton }
  }

  return (
    <div className={styles.wrapper}>
      <div className={[styles.inlineCalendar, error && styles.errorBorder].filter(Boolean).join(" ")}>
        {label && <div className={styles.calendarLabel}>{label}</div>}
        {hasPricing && (
          <div className={styles.legend}>
            <span className={styles.legendDot} />
            <Text type="secondary">
              {t("listings.tabs.preview.pricing_preview.discounted_days", { defaultValue: "Discounted days" })}
            </Text>
          </div>
        )}
        {isRange ? (
          <DayPicker
            {...commonProps}
            mode="range"
            // DayPicker's own min/max count the GAP between from/to (differenceInCalendarDays),
            // not the inclusive day count minDays/maxDays mean everywhere else in this app (and
            // everywhere else in this file, e.g. the hover-hint math below) — so a trip whose
            // maxDays is 8 needs max=7 here, or DayPicker allows a 9-day span (8 nights + both
            // endpoints). Same off-by-one applies to min.
            min={minDays ? minDays - 1 : undefined}
            max={effectiveMaxDays ? effectiveMaxDays - 1 : undefined}
            selected={rangeValue}
            onSelect={(range) => onChange(range)}
            defaultMonth={rangeValue?.from ?? dayjs().startOf("month").toDate()}
          />
        ) : (
          <DayPicker
            {...commonProps}
            mode="single"
            modifiers={spanModifiers}
            modifiersClassNames={{
              spanStart: styles.spanStart,
              spanMiddle: styles.spanMiddle,
              spanEnd: styles.spanEnd
            }}
            selected={value ? dayjs(value).toDate() : undefined}
            onSelect={(date) => {
              if (date) onChange(dayjs(date))
            }}
            defaultMonth={value ? dayjs(value).toDate() : dayjs().startOf("month").toDate()}
          />
        )}
      </div>
      {error && <Text className={styles.errorText}>{errorMessage || t("webIntegration.embedded_validation.date")}</Text>}
    </div>
  )
}

DateSelector.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  collapsed: PropTypes.bool,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  trip: PropTypes.object,
  isRange: PropTypes.bool,
  minDays: PropTypes.number,
  maxDays: PropTypes.number,
  maxSpanByDate: PropTypes.objectOf(PropTypes.number),
  availableDates: PropTypes.arrayOf(PropTypes.string),
  bookedDates: PropTypes.arrayOf(PropTypes.string),
  rangeValue: PropTypes.shape({ from: PropTypes.instanceOf(Date), to: PropTypes.instanceOf(Date) }),
  label: PropTypes.string,
  spanDays: PropTypes.number
}

export default React.memo(DateSelector)
